import React, { useEffect, useMemo, useState } from 'react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import {
  Typography,
  makeStyles,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useStyles as useOnboardingStyles } from '../OnboardingPage/utils/styles';
import { CAT_YELLOW, CAT_BLACK, CAT_BORDER, CAT_TEXT } from '../OnboardingPage/utils/styles';
import { DividerWithLabel } from '../OnboardingPage/utils/DividerWithLabel';
import { PocBox } from '../OnboardingPage/utils/PocBox';

// ─── Token type + client type domain ─────────────────────────────────────────
type TokenType = 'B2C' | 'AAD_V2' | 'AAD_V1' | 'PINGFED' | 'UNKNOWN';
type ClientType = 'USER_BASED' | 'SERVICE_BASED';
type ExpectedClientType = 'service' | 'user';

const MANDATORY_USER_FIELDS = [
  'catafltnorgcode',
  'catafltncode',
  'catrecid',
  'catloginid',
] as const;

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === '';

// Parse the issuer host so matching is anchored to the hostname, not an
// arbitrary substring of the full URL (which can be spoofed via sub/paths).
const getIssuerHost = (issuer: string): string | null => {
  try {
    return new URL(issuer).hostname.toLowerCase();
  } catch {
    return null;
  }
};

// True only for an exact host or a genuine subdomain of the given domain.
const hostMatchesDomain = (host: string, domain: string): boolean =>
  host === domain || host.endsWith(`.${domain}`);

const hostHasLabel = (host: string, label: string): boolean =>
  host.split('.').some(part => part === label || part.endsWith(label));

const identifyTokenType = (claims: Record<string, any>): TokenType => {
  const issuer = String(claims.iss ?? '');
  const host = getIssuerHost(issuer);
  const version = claims.ver != null ? String(claims.ver) : null;

  // B2C is recognized by its trust-framework-policy claim (tfp/acr) or the
  // /tfp/ path segment, since B2C is often served from a custom domain.
  const policy = claims.tfp ?? claims.acr;
  const issuerPath = (() => {
    try {
      return new URL(issuer).pathname.toLowerCase();
    } catch {
      return '';
    }
  })();
  const looksLikeB2C =
    (typeof policy === 'string' && /^b2c_/i.test(policy)) ||
    issuerPath.includes('/tfp/') ||
    /\/b2c_/i.test(issuerPath);
  if (looksLikeB2C) {
    return 'B2C';
  }

  if (!host) {
    return 'UNKNOWN';
  }
  if (hostMatchesDomain(host, 'b2clogin.com') || hostHasLabel(host, 'b2c')) {
    return 'B2C';
  }
  if (
    hostMatchesDomain(host, 'login.microsoftonline.com') ||
    hostMatchesDomain(host, 'sts.windows.net') ||
    hostMatchesDomain(host, 'microsoftonline.com')
  ) {
    return version === '2.0' ? 'AAD_V2' : 'AAD_V1';
  }
  if (hostMatchesDomain(host, 'pingidentity.com') || hostHasLabel(host, 'ping')) {
    return 'PINGFED';
  }
  return 'UNKNOWN';
};

type ValidationResult = {
  isValid: boolean;
  errors: string[];
  tokenType: TokenType;
  clientType: ClientType;
  actualClientId: string | null;
  effectiveClientIdForEntitlements: string | null;
  missingUserFields: string[];
};

const validateToken = (
  claims: Record<string, any>,
  expectedClientId: string,
  expectedClientType: ExpectedClientType,
): ValidationResult => {
  const errors: string[] = [];
  const tokenType = identifyTokenType(claims);

  // Determine client type (used for analysis even on early return).
  const clientType: ClientType =
    claims.name != null && (claims.mail != null || claims.email_address != null)
      ? 'USER_BASED'
      : 'SERVICE_BASED';

  const base: Omit<ValidationResult, 'isValid' | 'errors'> = {
    tokenType,
    clientType,
    actualClientId: claims.azp != null ? String(claims.azp) : null,
    effectiveClientIdForEntitlements: null,
    missingUserFields: [],
  };

  // 2. Reject unsupported / no-longer-valid token types.
  if (tokenType === 'AAD_V1') {
    errors.push('AAD-ENTRA ID V1 tokens are not supported');
    return { isValid: false, errors, ...base };
  }
  if (tokenType === 'PINGFED') {
    errors.push('PingFed tokens are no longer supported');
    return { isValid: false, errors, ...base };
  }
  if (tokenType === 'UNKNOWN') {
    errors.push('Unknown token type; unable to identify provider');
    return { isValid: false, errors, ...base };
  }

  // 3. B2C tokens must be user-based only.
  if (tokenType === 'B2C' && clientType === 'SERVICE_BASED') {
    errors.push('B2C tokens are only supported for user-based authentication');
  }

  // 3b. The token's detected client type must match the expected client type.
  const expectedClient: ClientType =
    expectedClientType === 'user' ? 'USER_BASED' : 'SERVICE_BASED';
  if (clientType !== expectedClient) {
    errors.push(
      `Token is ${clientType === 'USER_BASED' ? 'user based' : 'service based'}, but ` +
        `${expectedClient === 'USER_BASED' ? 'user based' : 'service based'} was expected`,
    );
  }

  // 4. Validate the client ID (claim is always "azp" for B2C / AAD_V2).
  const actualClientId = base.actualClientId;
  if (actualClientId === null) {
    errors.push('azp field is missing');
  } else if (expectedClientId && actualClientId !== expectedClientId) {
    errors.push(
      `Client ID mismatch: expected ${expectedClientId}, found ${actualClientId}`,
    );
  }

  // 5. Enforce mandatory user-based claims.
  let missingUserFields: string[] = [];
  if (clientType === 'USER_BASED') {
    missingUserFields = MANDATORY_USER_FIELDS.filter(f => isBlank(claims[f]));
    if (missingUserFields.length > 0) {
      errors.push(
        `Missing required fields for user-based token: ${missingUserFields.join(', ')}`,
      );
    }
  }

  // 6. Service-based effective identity (downstream entitlements only).
  let effectiveClientIdForEntitlements: string | null = null;
  if (clientType === 'SERVICE_BASED') {
    const legacyClientId = claims.legacy_client_id != null ? String(claims.legacy_client_id) : null;
    effectiveClientIdForEntitlements = legacyClientId ?? actualClientId;
  }

  // 7. cataccounttype, when present, must be TESTID (absent is allowed).
  const catAccountType = claims.cataccounttype;
  if (!isBlank(catAccountType) && String(catAccountType).trim() !== 'TESTID') {
    errors.push(
      `cataccounttype must be TESTID, found ${String(catAccountType).trim()}`,
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    ...base,
    missingUserFields,
    effectiveClientIdForEntitlements,
  };
};

// ─── JWT decoding helpers ─────────────────────────────────────────────────────
const base64UrlDecode = (segment: string): string => {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const withPadding = padded + '==='.slice((padded.length + 3) % 4);
  const binary = atob(withPadding);
  // Decode UTF-8 so non-ASCII claim values render correctly.
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
};

type DecodedJwt = {
  header: Record<string, any>;
  payload: Record<string, any>;
};

const decodeJwt = (token: string): DecodedJwt => {
  const trimmed = token.trim().replace(/^Bearer\s+/i, '');
  const parts = trimmed.split('.');
  if (parts.length < 2) {
    throw new Error('Invalid JWT: expected 3 dot-separated segments (header.payload.signature).');
  }
  try {
    const header = JSON.parse(base64UrlDecode(parts[0]));
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return { header, payload };
  } catch {
    throw new Error('Invalid JWT: unable to decode and parse token segments.');
  }
};

const formatEpoch = (value: unknown): string | null => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return new Date(num * 1000).toUTCString();
};

// ─── Styles (page shell mirrors the other standalone pages) ───────────────────
const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    background: '#f4f5f7',
  },
  orgBar: {
    position: 'sticky',
    top: 0,
    zIndex: 1300,
    backgroundColor: 'black',
    color: 'white',
    padding: '18px',
    borderLeft: '0.1px solid #535353',
    fontWeight: 700,
    fontSize: '30px',
  },
  hero: {
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(120deg, #1a1a1a 0%, #2b2b2b 60%, #383838 100%)',
    color: '#fff',
    padding: '28px 32px',
    borderBottom: '4px solid #FFCD11',
  },
  heroAccent: {
    position: 'absolute',
    top: -60,
    right: -40,
    width: 220,
    height: 220,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(255,205,17,0.18) 0%, rgba(255,205,17,0) 70%)',
    pointerEvents: 'none',
  },
  heroRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  heroIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 52,
    height: 52,
    borderRadius: 14,
    background: 'rgba(255,205,17,0.15)',
    border: '1px solid rgba(255,205,17,0.4)',
    flexShrink: 0,
  },
  heroTitle: {
    fontWeight: 800,
    fontSize: 26,
    lineHeight: 1.1,
    letterSpacing: '0.01em',
  },
  heroSubtitle: {
    marginTop: 4,
    fontSize: 14,
    color: 'rgba(255,255,255,0.7)',
  },
  body: {
    padding: '24px 32px 40px',
  },
  card: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #ececec',
    boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
    padding: 24,
    maxWidth: 1100,
    margin: '0 auto',
  },
});

const TOKEN_TYPE_LABELS: Record<TokenType, string> = {
  B2C: 'Azure AD B2C',
  AAD_V2: 'Entra ID (AAD) v2.0',
  AAD_V1: 'Entra ID (AAD) v1.0',
  PINGFED: 'PingFederate',
  UNKNOWN: 'Unknown',
};

const AnalysisRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'flex-start' }}>
    <Typography style={{ fontSize: 12.5, fontWeight: 700, color: CAT_TEXT, minWidth: 220, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography style={{ fontSize: 12.5, color: '#555', wordBreak: 'break-all' }}>{value}</Typography>
  </div>
);

export function TokenValidatorPage() {
  const classes = useStyles();
  const onboardingClasses = useOnboardingStyles();
  const orgName =
    useApi(configApiRef).getOptionalString('organization.name') ?? 'Service Catalog';

  const [expectedClientType, setExpectedClientType] = useState<ExpectedClientType>('service');
  const [token, setToken] = useState('');
  const [expectedClientId, setExpectedClientId] = useState('');
  const [decoded, setDecoded] = useState<DecodedJwt | null>(null);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [decodeError, setDecodeError] = useState<string | null>(null);

  const canValidate = token.trim().length > 0;

  const handleValidate = () => {
    setDecoded(null);
    setResult(null);
    setDecodeError(null);
    try {
      const jwt = decodeJwt(token);
      setDecoded(jwt);
      setResult(validateToken(jwt.payload, expectedClientId.trim(), expectedClientType));
    } catch (err: any) {
      setDecodeError(err?.message ?? 'Unable to decode token.');
    }
  };

  const handleClear = () => {
    setToken('');
    setExpectedClientId('');
    setDecoded(null);
    setResult(null);
    setDecodeError(null);
  };

  // Any change to a validation input invalidates the previously computed
  // result, so clear it to avoid showing a stale status beside new inputs.
  useEffect(() => {
    setDecoded(null);
    setResult(null);
    setDecodeError(null);
  }, [token, expectedClientId, expectedClientType]);

  // Liveness is informational only — this tool validates claims, not activity.
  const temporalNote = useMemo<string | null>(() => {
    const claimsPayload = decoded?.payload;
    if (!claimsPayload) return null;
    const nowSeconds = Math.floor(Date.now() / 1000);
    const exp = Number(claimsPayload.exp);
    if (Number.isFinite(exp) && exp > 0 && exp < nowSeconds) {
      return 'This token has expired. Its claims are still shown for reference.';
    }
    const nbf = Number(claimsPayload.nbf);
    if (Number.isFinite(nbf) && nbf > 0 && nbf > nowSeconds) {
      return 'This token is not yet active (nbf is in the future). Its claims are still shown for reference.';
    }
    return null;
  }, [decoded]);

  const claims = decoded?.payload ?? null;

  return (
    <div className={classes.page}>
      <div className={classes.orgBar}>{orgName}</div>

      <div className={classes.hero}>
        <div className={classes.heroAccent} />
        <div className={classes.heroRow}>
          <span className={classes.heroIcon}>
            <VerifiedUserIcon style={{ color: '#FFCD11', fontSize: 30 }} />
          </span>
          <div>
            <Typography className={classes.heroTitle}>Token Validator</Typography>
            <Typography className={classes.heroSubtitle}>
              Decode a JWT access token and verify it against Service Catalog requirements.
            </Typography>
          </div>
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.card}>
          <Typography variant="body2" style={{ color: '#757575', marginBottom: 20 }}>
            Paste a JWT access token below and select the client type you expect. The token is decoded
            locally in your browser — nothing is sent to any server or stored.
          </Typography>

          {/* Client type selector */}
          <div
            style={{
              border: `1px solid ${CAT_BORDER}`,
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 16,
              background: '#FAFAFA',
            }}
          >
            <Typography style={{ fontSize: 12.5, fontWeight: 700, color: CAT_TEXT, marginBottom: 6 }}>
              Expected client type
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={expectedClientType === 'service'}
                    onChange={() => setExpectedClientType('service')}
                    style={{ color: CAT_YELLOW }}
                  />
                }
                label={<span style={{ fontSize: 13 }}>Service based (client_credentials)</span>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={expectedClientType === 'user'}
                    onChange={() => setExpectedClientType('user')}
                    style={{ color: CAT_YELLOW }}
                  />
                }
                label={<span style={{ fontSize: 13 }}>User based (authorization_code)</span>}
              />
            </div>
          </div>

          {/* Inputs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
            <div>
              <Typography style={{ fontSize: 12, fontWeight: 700, color: CAT_TEXT, marginBottom: 5 }}>
                JWT Token
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                multiline
                minRows={4}
                value={token}
                onChange={e => setToken(e.target.value)}
                placeholder="Paste your access token (eyJ...). A leading 'Bearer ' is ignored."
                InputProps={{ style: { fontSize: 12.5, borderRadius: 8, fontFamily: 'monospace' } }}
              />
            </div>
            <div>
              <Typography style={{ fontSize: 12, fontWeight: 700, color: CAT_TEXT, marginBottom: 5 }}>
                Expected Client ID{' '}
                <span style={{ fontWeight: 400, color: '#9E9E9E' }}>(optional — compared against the azp claim)</span>
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                value={expectedClientId}
                onChange={e => setExpectedClientId(e.target.value)}
                placeholder="e.g. 1234abcd-12ab-34cd-56ef-1234567890ab"
                InputProps={{ style: { fontSize: 13.5, borderRadius: 8 } }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
            <Button
              variant="contained"
              disabled={!canValidate}
              onClick={handleValidate}
              style={{
                background: canValidate ? CAT_YELLOW : '#E0E0E0',
                color: canValidate ? CAT_BLACK : '#999',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 8,
                padding: '8px 24px',
                fontSize: 13.5,
                boxShadow: 'none',
                minWidth: 160,
              }}
            >
              Validate Token
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
              style={{ textTransform: 'none', borderRadius: 8, fontSize: 13, borderColor: CAT_BORDER, color: CAT_TEXT, padding: '7px 16px' }}
            >
              Clear
            </Button>
          </div>

          {/* Decode error */}
          {decodeError && (
            <div style={{ background: '#FFF3F3', border: '1px solid #F5C6CB', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              <Typography style={{ fontSize: 13, color: '#B00020', lineHeight: 1.6 }}>{decodeError}</Typography>
            </div>
          )}

          {/* Validation status */}
          {result && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                background: result.isValid ? '#EDF7EE' : '#FFF3F3',
                border: `1px solid ${result.isValid ? '#A5D6A7' : '#F5C6CB'}`,
                borderRadius: 8,
                padding: '14px 18px',
                marginBottom: 16,
              }}
            >
              {result.isValid ? (
                <CheckCircleIcon style={{ color: '#2E7D32', fontSize: 22, flexShrink: 0 }} />
              ) : (
                <ErrorOutlineIcon style={{ color: '#B00020', fontSize: 22, flexShrink: 0 }} />
              )}
              <div>
                <Typography style={{ fontSize: 14, fontWeight: 700, color: result.isValid ? '#2E7D32' : '#B00020' }}>
                  {result.isValid ? 'Token is valid' : 'Token is invalid'}
                </Typography>
                {result.errors.length > 0 && (
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                    {result.errors.map(err => (
                      <li key={err} style={{ fontSize: 13, color: '#B00020', lineHeight: 1.6 }}>{err}</li>
                    ))}
                  </ul>
                )}
                {temporalNote && (
                  <Typography style={{ fontSize: 12.5, color: '#8D6E00', marginTop: 6 }}>
                    Note: {temporalNote}
                  </Typography>
                )}
                <Typography style={{ fontSize: 12.5, color: '#5D4037', marginTop: 6 }}>
                  Note: only the token payload (claims) is validated. The cryptographic signature is not
                  verified, so this check does not confirm the token is authentic or untampered.
                </Typography>
              </div>
            </div>
          )}

          {/* Token analysis */}
          {result && claims && (
            <>
              <DividerWithLabel label="Token Analysis" classes={onboardingClasses} />

              <div
                style={{
                  border: `1px solid ${CAT_BORDER}`,
                  borderRadius: 8,
                  padding: '16px 18px',
                  marginBottom: 16,
                  background: '#FAFAFA',
                }}
              >
                <AnalysisRow label="Token type" value={TOKEN_TYPE_LABELS[result.tokenType]} />
                <AnalysisRow
                  label="Detected client type"
                  value={result.clientType === 'USER_BASED' ? 'User based' : 'Service based'}
                />
                <AnalysisRow label="Issuer (iss)" value={claims.iss ?? '—'} />
                <AnalysisRow label="Version (ver)" value={claims.ver ?? '—'} />
                <AnalysisRow label="Audience (aud)" value={claims.aud ?? '—'} />
                <AnalysisRow
                  label="Client ID (azp)"
                  value={
                    result.actualClientId ?? (
                      <span style={{ color: '#B00020' }}>missing</span>
                    )
                  }
                />
                {expectedClientId.trim() && result.actualClientId && (
                  <AnalysisRow
                    label="Client ID match"
                    value={
                      result.actualClientId === expectedClientId.trim() ? (
                        <span style={{ color: '#2E7D32' }}>matches expected</span>
                      ) : (
                        <span style={{ color: '#B00020' }}>does not match expected</span>
                      )
                    }
                  />
                )}
                {result.clientType === 'SERVICE_BASED' && (
                  <AnalysisRow
                    label="Effective client ID (entitlements)"
                    value={result.effectiveClientIdForEntitlements ?? '—'}
                  />
                )}
                {result.clientType === 'USER_BASED' && (
                  <AnalysisRow
                    label="Mandatory user fields"
                    value={
                      result.missingUserFields.length === 0 ? (
                        <span style={{ color: '#2E7D32' }}>all present</span>
                      ) : (
                        <span style={{ color: '#B00020' }}>
                          missing: {result.missingUserFields.join(', ')}
                        </span>
                      )
                    }
                  />
                )}
                <AnalysisRow label="Subject (sub)" value={claims.sub ?? '—'} />
                <AnalysisRow label="Name" value={claims.name ?? '—'} />
                <AnalysisRow label="Mail" value={claims.mail ?? claims.email_address ?? '—'} />
                <AnalysisRow
                  label="Account type (cataccounttype)"
                  value={
                    isBlank(claims.cataccounttype) ? (
                      '—'
                    ) : String(claims.cataccounttype).trim() === 'TESTID' ? (
                      <span style={{ color: '#2E7D32' }}>{claims.cataccounttype}</span>
                    ) : (
                      <span style={{ color: '#B00020' }}>{claims.cataccounttype} (must be TESTID)</span>
                    )
                  }
                />
                <AnalysisRow label="Issued at (iat)" value={formatEpoch(claims.iat) ?? '—'} />
                <AnalysisRow label="Not before (nbf)" value={formatEpoch(claims.nbf) ?? '—'} />
                <AnalysisRow
                  label="Expires (exp)"
                  value={
                    formatEpoch(claims.exp) ? (
                      <>
                        {formatEpoch(claims.exp)}{' '}
                        {Number(claims.exp) * 1000 < Date.now() ? (
                          <span style={{ color: '#B00020' }}>(expired)</span>
                        ) : (
                          <span style={{ color: '#2E7D32' }}>(active)</span>
                        )}
                      </>
                    ) : (
                      '—'
                    )
                  }
                />
              </div>

              <Typography style={{ fontSize: 13, fontWeight: 700, color: CAT_TEXT, marginBottom: 10 }}>
                Decoded claims (payload)
              </Typography>
              <div style={{ background: '#1A1A1A', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
                <pre style={{ margin: 0, padding: '12px 16px', fontSize: 12.5, color: '#E8E8E8', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.7 }}>
                  {JSON.stringify(claims, null, 2)}
                </pre>
              </div>
            </>
          )}

          <PocBox
            classes={onboardingClasses}
            contact="Federation Services Support Team"
            email="Federation_Services_Support@cat.com"
            issues={[
              'Token type not supported',
              'Client ID mismatch',
              'Missing required user claims',
              'Token expired',
            ]}
          />
        </div>
      </div>
    </div>
  );
}

export default TokenValidatorPage;
